import React, { ReactNode } from 'react';
import { Box, Typography, IconButton, Collapse } from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import { DataTableColumn, DataTableSelection, DataTableExpansion, NestedTableRenderProps } from '../tableTypes';
import { toKeyValuePairs, renderCardMultiLinePairs } from '@core/utils/tableUtils.tsx';
import { useTranslation } from 'react-i18next';
import WcnCheckbox from '@/components/shared/WcnCheckbox';

interface WcnMobileCardsProps<T> {
    readonly paginatedData: T[];
    readonly columns: DataTableColumn<T>[];
    readonly selection?: DataTableSelection<T>;
    readonly expansion?: DataTableExpansion<T>;
    readonly expandedRows: (string | number)[];
    readonly hasExpandableRows: boolean;
    readonly mobileView: 'auto' | 'scroll' | 'card';
    readonly loading?: boolean;
    readonly noDataMessage?: string;
    readonly renderEmpty?: () => ReactNode;
    readonly onRowClick?: (row: T, index: number) => void;
    readonly onRowSelection: (row: T, event?: React.MouseEvent) => void;
    readonly onRowExpansion: (row: T, event?: React.MouseEvent) => void;
    /** Renders a nested table for expanded rows (keeps card view consistent). */
    readonly renderNestedTable: (props: NestedTableRenderProps) => ReactNode;
}

function WcnMobileCards<T>({
    paginatedData,
    columns,
    selection,
    expansion,
    expandedRows,
    hasExpandableRows,
    mobileView,
    loading,
    noDataMessage,
    renderEmpty,
    onRowClick,
    onRowSelection,
    onRowExpansion,
    renderNestedTable
}: Readonly<WcnMobileCardsProps<T>>) {
    const { t } = useTranslation();

    if (!paginatedData || paginatedData.length === 0) {
        return (
            <Box className="wcn-table-mobile-empty">
                {renderEmpty ? (
                    renderEmpty()
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        {loading ? t('loadingData') : noDataMessage}
                    </Typography>
                )}
            </Box>
        );
    }

    const dataColumns = columns.filter((col) => !col.isAction && !col.isTitle);
    const actionColumns = columns.filter((col) => col.isAction);
    const titleColumn = columns.find((col) => col.isTitle);

    return (
        <Box className="wcn-table-mobile-container">
            {paginatedData.map((row, rowIndex) => {
                const isSelected = selection
                    ? selection.selectedItems.some((item) => selection.getItemKey(item) === selection.getItemKey(row))
                    : false;
                const rowKey = expansion?.getRowKey(row) ?? rowIndex;
                const isExpanded = expandedRows.includes(rowKey);
                const showExpandIcon = expansion?.showExpandIconCondition?.(row) ?? true;
                const rowTitle =
                    (titleColumn?.render
                        ? titleColumn.render(row, rowIndex)
                        : (row as any)[titleColumn?.value ?? '']) ?? String(rowIndex + 1).padStart(2, '0');

                return (
                    <Box
                        key={rowKey}
                        className="wcn-table-mobile-card"
                        onClick={(e: React.MouseEvent) => {
                            if (selection && !expansion) onRowSelection(row, e);
                            onRowClick?.(row, rowIndex);
                        }}
                    >
                        <Box
                            className="wcn-table-mobile-card-main"
                            sx={{ '&:focus-within': { position: 'relative', zIndex: 3 } }}
                        >
                            {!selection && actionColumns.length > 0 && (
                                <Box className="wcn-table-mobile-card-header">
                                    <Typography variant="body2" fontWeight={600} color="text.secondary" component="div">
                                        {rowTitle}
                                    </Typography>
                                    <Box className="wcn-table-mobile-card-header-actions">
                                        {actionColumns.map((col) => (
                                            <Box key={col.value}>
                                                {col.render ? col.render(row, rowIndex) : (row as any)[col.value]}
                                            </Box>
                                        ))}
                                        {hasExpandableRows && showExpandIcon && (
                                            <IconButton
                                                size="small"
                                                onClick={(e: React.MouseEvent) => onRowExpansion(row, e)}
                                            >
                                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                        )}
                                    </Box>
                                </Box>
                            )}

                            {dataColumns.map((column, colIndex) => {
                                const isLastRow = colIndex === dataColumns.length - 1;
                                const cellTitle = typeof column.title === 'function' ? column.title(row) : column.title;

                                if (column.isHidden?.(row) ?? false) return;

                                if (column.multiLine) {
                                    let multiLineData;
                                    if (column.multiLineValue) {
                                        multiLineData = column.multiLineValue(row);
                                    } else if (column.render) {
                                        multiLineData = column.render(row, rowIndex);
                                    } else {
                                        multiLineData = (row as any)[column.value];
                                    }
                                    const pairs = toKeyValuePairs(multiLineData);
                                    return (
                                        <Box key={column.value} title={cellTitle} className="wcn-table-mobile-card-row">
                                            <Box
                                                className="wcn-card-label wcn-card-label-multiline"
                                                sx={{ bgcolor: 'action.hover' }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    fontWeight={600}
                                                    sx={{ color: 'text.secondary' }}
                                                >
                                                    {column.text}
                                                </Typography>
                                            </Box>
                                            <Box className="wcn-card-value wcn-card-value-multiline">
                                                {renderCardMultiLinePairs(pairs)}
                                            </Box>
                                        </Box>
                                    );
                                }

                                return (
                                    <Box
                                        key={column.value}
                                        title={cellTitle}
                                        className="wcn-table-mobile-card-row"
                                        sx={{ borderBottom: isLastRow ? 'none' : undefined }}
                                    >
                                        <Box className="wcn-card-label" sx={{ bgcolor: 'action.hover' }}>
                                            <Typography
                                                variant="body2"
                                                fontWeight={600}
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {column.text}
                                            </Typography>
                                        </Box>
                                        <Box className="wcn-card-value" sx={{ bgcolor: 'background.paper' }}>
                                            <Typography
                                                variant="body2"
                                                component="div"
                                                sx={{ wordBreak: 'break-word' }}
                                            >
                                                {column.render
                                                    ? column.render(row, rowIndex)
                                                    : (row as any)[column.value]}
                                            </Typography>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>

                        {selection && (
                            <Box
                                className={`wcn-table-mobile-selection ${isSelected ? 'selected' : ''}`}
                                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                            >
                                <WcnCheckbox
                                    checked={isSelected}
                                    size="small"
                                    className="table-checkbox"
                                    disableRipple
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation();
                                        onRowSelection(row, e);
                                    }}
                                />
                            </Box>
                        )}

                        {expansion && isExpanded && (
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box>
                                    {expansion.nestedTable
                                        ? renderNestedTable({
                                              columns: expansion.nestedTable.columns,
                                              data: expansion.nestedTable.getChildRows(row, expansion.nestedTable.data),
                                              size: expansion.nestedTable.size ?? 'small',
                                              noDataMessage: expansion.nestedTable.noDataMessage,
                                              expansion: expansion.nestedTable.expansion,
                                              mobileView
                                          })
                                        : expansion.renderExpandedContent?.(row)}
                                </Box>
                            </Collapse>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
}

WcnMobileCards.displayName = 'WcnMobileCards';

export default React.memo(WcnMobileCards) as typeof WcnMobileCards;
